import type { ApiError } from './apiClient';

/**
 * Why a read failed, as far as the reader needs to know: the HTTP status and
 * the API's machine-readable `code`, never its prose. A panel branches on these
 * to say "busy" or "couldn't load" — the server's own message ("Internal server
 * error", a driver string) is not copy.
 */
export interface LoadFailure {
  status: number | null;
  code: string | null;
}

/** The code `loadFailureOf` gives a request the client abandoned after its stall backstop. */
export const REQUEST_TIMEOUT_CODE = 'REQUEST_TIMEOUT';

const BUSY_CODES: ReadonlySet<string> = new Set(['DB_BUSY', 'DB_TIMEOUT', REQUEST_TIMEOUT_CODE]);

export function loadFailureOf(err: unknown): LoadFailure {
  if (!(err instanceof Error)) return { status: null, code: null };
  // A client-side timeout is a stall, not a server verdict; it reads as busy.
  if (err.name === 'TimeoutError') return { status: null, code: REQUEST_TIMEOUT_CODE };
  const { status, code } = err as ApiError;
  return {
    status: typeof status === 'number' ? status : null,
    code: typeof code === 'string' ? code : null,
  };
}

/** A 503 (`DB_BUSY` / `DB_TIMEOUT`) or a stalled request: worth trying again in a moment. */
export function isBusyFailure({ status, code }: Partial<LoadFailure>): boolean {
  return status === 503 || (code != null && BUSY_CODES.has(code));
}

/** The failure fields a data hook publishes beside its unchanged `error` string. */
export interface LoadFailureState {
  error: string | null;
  errorStatus: number | null;
  errorCode: string | null;
}

export const NO_FAILURE: LoadFailureState = { error: null, errorStatus: null, errorCode: null };

/** `message` is for logs and legacy readers; panels render `ReportLoadError`, never this string. */
export function failureStateOf(err: unknown, message: string): LoadFailureState {
  const { status, code } = loadFailureOf(err);
  return { error: message, errorStatus: status, errorCode: code };
}

/**
 * {@link failureStateOf} for a hook whose `error` string a surface still prints:
 * the thrown message where there is one, else `fallback`, as `useApiResource`
 * has always set it.
 */
export function failureStateOfMessage(err: unknown, fallback: string): LoadFailureState {
  return failureStateOf(err, err instanceof Error ? err.message : fallback);
}
